// Pick art so the same picture does not greet the player twice in a row.
//
// `pickFresh` draws at random from a pool but never returns what the same
// `key` showed last time (when the pool has an alternative). The memory is per
// session and in-process only: nothing to persist, nothing to clear. Pass a
// seeded `rand` in tests.

const lastShown = new Map<string, string>();

/** A random member of `pool`, different from the last one shown under `key`. */
export function pickFresh<T extends string>(
  key: string,
  pool: readonly T[],
  rand: () => number = Math.random,
): T {
  if (pool.length === 0) throw new Error(`artPick: empty pool for "${key}"`);
  const at = (r: number) => Math.min(pool.length - 1, Math.floor(r * pool.length));
  let i = at(rand());
  const previous = lastShown.get(key);
  if (pool.length > 1 && pool[i] === previous) {
    // Step to a random *other* member.
    i = (i + 1 + Math.floor(rand() * (pool.length - 1))) % pool.length;
  }
  const choice = pool[i] as T;
  lastShown.set(key, choice);
  return choice;
}

/** True when `id` is what `key` showed last. */
export function wasJustShown(key: string, id: string): boolean {
  return lastShown.get(key) === id;
}

/** Record that `id` was shown under `key` (for picks made outside `pickFresh`). */
export function markShown(key: string, id: string): void {
  lastShown.set(key, id);
}

/** Forget everything (tests). */
export function resetArtMemory(): void {
  lastShown.clear();
}
